<template>
    <div>
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                v-if="model"
                ref="RkmForm"
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field='id'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.lotacoes"
                            @change="handleLotacaoChange"
                            default-option-text="Todos"
                            label="CBO"
                            show-default-option
                            text-field="nome"
                            v-model="model.ocupacaoId"
                            value-field='id'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.papeis"
                            @change="handlePapelChange"
                            default-option-text="Todos"
                            label="Papel"
                            show-default-option
                            text-field="nome"
                            v-model="model.papelId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.acoes"
                            @change="handleAcaoChange"
                            default-option-text="Todas"
                            label="Tipo de ação"
                            show-default-option
                            text-field="nome"
                            v-model="model.acaoId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :repository="repositories.relatorioHistoricoPapelLotacao.usuario"
                            :request-query-string-params="requestQueryStringParamsUsuario"
                            :search-fields="[{ key: 'cadastro_cidadao.nome', searchable: true }]"
                            label="Usuário"
                            v-model="model.usuario"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :repository="repositories.relatorioHistoricoPapelLotacao.usuarioResponsavel"
                            :request-query-string-params="requestQueryStringParamsUsuarioResponsavel"
                            :search-fields="[{ key: 'cadastro_cidadao.nome', searchable: true }]"
                            label="Permitido por"
                            v-model="model.usuarioResponsavel"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-button
                            :label="false"
                            @click="handleBuscarClick"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="handleLimparClick"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="handleTodosClick"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="handleImprimirClick"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
            />
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import {error, formats, object, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        fields() {
            return this.columns.filter(column => (column.fixed || false) || this.selectedColumns.includes(column.key))
        },
        optionalColumns() {
            return this.columns.filter(column => !(column.fixed || false))
        },
        validations() {
            return {
                periodo: { required: true },
            }
        },
        requestQueryStringParamsUsuario() {
            const request = this.clone(this.model)

            request.periodo.start = this.others.moment(this.model.periodo.start).format('DD/MM/YYYY')
            request.periodo.end = this.others.moment(this.model.periodo.end).format('DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(request)
        },
        requestQueryStringParamsUsuarioResponsavel() {
            const request = this.clone(this.model)

            request.periodo.start = this.others.moment(this.model.periodo.start).format('DD/MM/YYYY')
            request.periodo.end = this.others.moment(this.model.periodo.end).format('DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(request)
        },
    },
    created() {
        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.administracao'),
            relatorioHistoricoPapelLotacao: this.getConectaSusRepository('administracao.relatorioHistoricoPapelLotacao'),
        }
    },
    data() {
        return {
            additionals: null,
            columns: [
                {
                    class: 'col-min text-center',
                    filterByFormatted: true,
                    fixed: true,
                    formatter: value => this.formatDateTime(value),
                    key: 'historicoDataHora',
                    label: 'Data/Hora',
                    sortable: true,
                },
                {key: 'unidadeNomeFantasia', label: 'Unidade', fixed: true, sortable: true },
                {key: 'infraestruturaNome', label: 'Local de atendimento', fixed: true, sortable: true },
                {key: 'ocupacaoNome', label: 'CBO', fixed: true, sortable: true },
                {key: 'papelNome', label: 'Papel', fixed: true, sortable: true },
                {key: 'acaoNome', label: 'Tipo de ação', fixed: true, sortable: true },
                {key: 'usuarioNome', label: 'Usuário', fixed: true, sortable: true },
                {key: 'usuarioResponsavelNome', label: 'Permitido por', sortable: true },
            ],
            form: null,
            indeterminate: true,
            itens: [],
            model: null,
            others: {moment},
            selectedAll: false,
            selectedColumns: [],
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioHistoricoPapelLotacao.create()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange() {
            this.form.infraestruturas = []
            this.form.lotacoes = []
            this.form.papeis = []
            this.form.acoes = []
            this.model.infraestruturaId = null
            this.model.lotacaoId = null
            this.model.ocupacaoId = null
            this.model.papelId = null
            this.model.acaoId = null

            this.model.usuario = {
                id: null,
                nome: null,
            }

            this.model.usuarioResponsavel = {
                id: null,
                nome: null
            }

            this.itens = []

            this.getInfraestruturas()
        },
        handleInfraestruturaChange() {
            this.form.lotacoes = []
            this.form.papeis = []
            this.form.acoes = []
            this.model.lotacaoId = null
            this.model.ocupacaoId = null
            this.model.papelId = null
            this.model.acaoId = null

            this.model.usuario = {
                id: null,
                nome: null,
            }

            this.model.usuarioResponsavel = {
                id: null,
                nome: null
            }

            this.itens = []

            this.getLotacoes()
        },
        handleLotacaoChange() {
            this.form.papeis = []
            this.form.acoes = []
            this.model.papelId = null
            this.model.acaoId = null

            this.model.usuario = {
                id: null,
                nome: null,
            }

            this.model.usuarioResponsavel = {
                id: null,
                nome: null
            }

            this.itens = []

            this.getPapeis()
        },
        handlePapelChange() {
            this.form.acoes = []
            this.model.acaoId = null

            this.model.usuario = {
                id: null,
                nome: null,
            }

            this.model.usuarioResponsavel = {
                id: null,
                nome: null
            }

            this.itens = []

            this.getAcoes()
        },
        handleAcaoChange() {
            this.model.usuario = {
                id: null,
                nome: null,
            }

            this.model.usuarioResponsavel = {
                id: null,
                nome: null
            }

            this.itens = []
        },
        handleBuscarClick() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    this.itens = []

                    const request = this.clone(this.model)

                    request.periodo.start = this.others.moment(this.model.periodo.start).format('DD/MM/YYYY')
                    request.periodo.end = this.others.moment(this.model.periodo.end).format('DD/MM/YYYY')

                    this.setLoading()

                    this.repositories.relatorioHistoricoPapelLotacao.index(this.camelObjectKeysToSnakeRecursive(request))
                        .then(response => {
                            this.itens = response.data
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        handleLimparClick() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.lotacoes = []
            this.form.papeis = []
            this.form.acoes = []

            this.model.periodo = {
                start: null,
                end: null,
            }

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.lotacaoId = null
            this.model.ocupacaoId = null
            this.model.papelId = null
            this.model.acaoId = null

            this.model.usuario = {
                id: null,
                nome: null,
            }

            this.model.usuarioResponsavel = {
                id: null,
                nome: null
            }

            this.itens = []

            this.$nextTick().then(() => this.$refs.RkmForm.resetValidator())
        },
        handleTodosClick(selected) {
            this.selectedColumns = selected ? this.optionalColumns.map(c => c.key) : []
        },
        handleImprimirClick() {
            this.setLoading()

            const request = {
                ...this.request(),
                selectedColumns: this.fields.map(field => ({
                    class: field.class,
                    key: this.camelToSnake(field.key),
                    label: field.label,
                })),
            }

            this.repositories.impressao.relatorioHistoricoPapelLotacao(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        request() {
            const model = this.clone(this.model)

            model.periodo.start = this.others.moment(model.periodo.start).format('DD/MM/YYYY')
            model.periodo.end = this.others.moment(model.periodo.end).format('DD/MM/YYYY')

            return model
        },
        getUnidades() {
            this.setLoading()

            const request = {
                listar: 'unidades',
                ...this.request(),
            }

            this.repositories.relatorioHistoricoPapelLotacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.unidades = Object.assign(response.data.unidades)
                    this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                    this.form.lotacoes = Object.assign(response.data.lotacoes)
                    this.form.papeis = Object.assign(response.data.papeis)
                    this.form.acoes = Object.assign(response.data.acoes)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getInfraestruturas() {
            this.setLoading()

            const request = {
                listar: 'infraestruturas',
                ...this.request(),
            }

            this.repositories.relatorioHistoricoPapelLotacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                    this.form.lotacoes = Object.assign(response.data.lotacoes)
                    this.form.papeis = Object.assign(response.data.papeis)
                    this.form.acoes = Object.assign(response.data.acoes)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getLotacoes() {
            this.setLoading()

            const request = {
                listar: 'lotacoes',
                ...this.request(),
            }

            this.repositories.relatorioHistoricoPapelLotacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.lotacoes = Object.assign(response.data.lotacoes)
                    this.form.papeis = Object.assign(response.data.papeis)
                    this.form.acoes = Object.assign(response.data.acoes)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getPapeis() {
            this.setLoading()

            const request = {
                listar: 'papeis',
                ...this.request(),
            }

            this.repositories.relatorioHistoricoPapelLotacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.papeis = Object.assign(response.data.papeis)
                    this.form.acoes = Object.assign(response.data.acoes)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getAcoes() {
            this.setLoading()

            const request = {
                listar: 'acoes',
                ...this.request(),
            }

            this.repositories.relatorioHistoricoPapelLotacao.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.acoes = Object.assign(response.data.acoes)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mounted() {
        this.load()
    },
    mixins: [error, formats, object, repository, stringCase],
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                this.form.unidades = []
                this.form.infraestruturas = []
                this.form.lotacoes = []
                this.form.papeis = []
                this.form.acoes = []
                this.model.unidadeId = null
                this.model.infraestruturaId = null
                this.model.lotacaoId = null
                this.model.ocupacaoId = null
                this.model.papelId = null
                this.model.acaoId = null

                this.model.usuario = {
                    id: null,
                    nome: null,
                }

                this.model.usuarioResponsavel = {
                    id: null,
                    nome: null
                }

                this.itens = []

                if (value.start && value.end) {
                    this.getUnidades()
                }
            },
        },
        'model.usuario': {
            deep: true,
            handler() {
                this.model.usuarioResponsavel = {
                    id: null,
                    nome: null
                }

                this.itens = []
            },
        },
        'model.usuarioResponsavel': {
            deep: true,
            handler() {
                this.itens = []
            },
        },
        selectedColumns(value) {
            this.indeterminate = value.length > 0 && value.length != this.optionalColumns.length
            this.selectedAll = value.length > 0 && value.length == this.optionalColumns.length
        },
    },
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 300px;
}
</style>